import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setToken } from '@/store/slices/authSlice';
import { tokenSelector } from '@/store/selectors';
import { resolveItemKey } from '@/lib/resolveItemKey';
import { api, AuthError } from '@/lib/api';
import { usePollingState } from '@/lib/usePollingState';
import { Sidebar } from '@/components/Sidebar';
import { DetailPane } from '@/components/DetailPane';
import { DirectEditPane } from '@/components/DirectEditPane';
import { NewArticleDialog } from '@/components/NewArticleDialog';
import { ArticleSearchDialog } from '@/components/ArticleSearchDialog';
import { NotAuthorized } from '@/components/NotAuthorized';
import { Spinner } from '@/components/Spinner';
import { Toaster } from '@/components/ui/sonner';
import type { DashboardItem, DashboardState, ItemStatus } from '@shared/types';

type DirectEdit = {
  filePath: string;
  initialContent: string;
  prTitle: string;
  isEdit: boolean;
};

function keyOf(item: DashboardItem) {
  return `${item.kind}-${item.number}`;
}

export function App() {
  const token = useAppSelector(tokenSelector);
  const dispatch = useAppDispatch();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [requestedItem] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('item');
  });
  const [optimisticStatus, setOptimisticStatus] = useState<
    Record<string, ItemStatus>
  >({});
  const [directEdit, setDirectEdit] = useState<DirectEdit | null>(null);
  const [newArticleOpen, setNewArticleOpen] = useState(false);
  const [editExistingOpen, setEditExistingOpen] = useState(false);

  const { data, refresh } = usePollingState<DashboardState>({
    intervalMs: 20_000,
    fetcher: () => {
      if (!token) return Promise.reject(new AuthError('no-token'));
      return api.state(token);
    },
    onError: (e) => {
      if (e instanceof AuthError) {
        dispatch(setToken(null));
        return;
      }
      const msg = e instanceof Error ? e.message : 'Failed to load';
      toast.error('Could not refresh', { description: msg });
    },
  });

  const isLoading = data === null;

  // Apply optimistic status overrides; clear an override once the polled
  // status has moved away from 'needs-review' (the workflow has caught up).
  const items = useMemo(() => {
    const raw = data?.items ?? [];
    return raw.map((it) => {
      const override = optimisticStatus[keyOf(it)];
      return override && it.status === 'needs-review'
        ? { ...it, status: override }
        : it;
    });
  }, [data, optimisticStatus]);

  useEffect(() => {
    if (!data) return;
    setOptimisticStatus((prev) => {
      let changed = false;
      const next: Record<string, ItemStatus> = { ...prev };
      for (const key of Object.keys(next)) {
        const item = data.items.find((i) => keyOf(i) === key);
        if (!item || item.status !== 'needs-review') {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [data]);

  const markOptimistic = (key: string, status: ItemStatus) => {
    setOptimisticStatus((prev) => ({ ...prev, [key]: status }));
  };

  const navGuardRef = useRef<(action: () => void) => void>((a) => a());
  const registerNavigationGuard = useCallback(
    (guard: (action: () => void) => void) => { navGuardRef.current = guard; },
    [],
  );

  // Auto-select: prefer the URL-requested item, fall back to first.
  useEffect(() => {
    if (selectedKey || items.length === 0) return;
    if (requestedItem) {
      const resolved = resolveItemKey(requestedItem, items);
      if (resolved) {
        setSelectedKey(resolved);
        return;
      }
    }
    setSelectedKey(keyOf(items[0]));
  }, [items, selectedKey, requestedItem]);

  const selected = useMemo(
    () => items.find((i) => keyOf(i) === selectedKey) ?? null,
    [items, selectedKey],
  );

  if (!token) return <NotAuthorized />;

  const handleStartEditing = (filePath: string, initialContent: string, prTitle: string, isEdit = false) => {
    setDirectEdit({ filePath, initialContent, prTitle, isEdit });
  };

  const handleFirstSave = ({ prNumber }: { prNumber: number; commitSha: string }) => {
    setDirectEdit(null);
    refresh();
    setSelectedKey(`pr-${prNumber}`);
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        items={items}
        selectedKey={selectedKey}
        loading={isLoading}
        onSelect={(i) => navGuardRef.current(() => { setDirectEdit(null); setSelectedKey(keyOf(i)); })}
        onCreateNew={() => navGuardRef.current(() => setNewArticleOpen(true))}
        onEditExisting={() => navGuardRef.current(() => setEditExistingOpen(true))}
      />

      {directEdit ? (
        <DirectEditPane
          filePath={directEdit.filePath}
          initialContent={directEdit.initialContent}
          prTitle={directEdit.prTitle}
          isEdit={directEdit.isEdit}
          token={token}
          onFirstSave={handleFirstSave}
          onCancel={() => setDirectEdit(null)}
          onAuthError={() => { dispatch(setToken(null)); setDirectEdit(null); }}
          registerNavigationGuard={registerNavigationGuard}
        />
      ) : selected ? (
        <DetailPane
          item={selected}
          token={token}
          onAfterAction={refresh}
          onOptimisticStatus={(status) =>
            markOptimistic(keyOf(selected), status)
          }
          registerNavigationGuard={registerNavigationGuard}
        />
      ) : (
        <main className="flex-1 flex items-center justify-center text-muted-foreground">
          {isLoading ? (
            <Spinner label="Loading items…" />
          ) : items.length === 0 ? (
            'Nothing in flight.'
          ) : (
            'Select an item from the sidebar.'
          )}
        </main>
      )}

      <NewArticleDialog
        open={newArticleOpen}
        token={token ?? ''}
        onClose={() => setNewArticleOpen(false)}
        onAuthError={() => dispatch(setToken(null))}
        onStartEditing={handleStartEditing}
        onCreated={(res) => { setNewArticleOpen(false); handleFirstSave(res); }}
      />

      <ArticleSearchDialog
        open={editExistingOpen}
        token={token}
        onClose={() => setEditExistingOpen(false)}
        onSelectArticle={(fp, ic, pt) => handleStartEditing(fp, ic, pt, true)}
        onAuthError={() => dispatch(setToken(null))}
      />

      <Toaster />
    </div>
  );
}
